import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Typography, Box, Button, Container, Dialog, DialogActions, DialogContent, DialogTitle } from '@mui/material';
import { Appointment } from '../types';
import AppointmentCard from '../components/employee/components/MyAppointments/AppointmentCard';
import LoadingSpinner from '../components/global/LoadingSpinner';
import ErrorDialog from '../components/global/ErrorDialog';
import { useAdminData } from '../contexts/AdminDataContext';
import { useQueryClient } from '@tanstack/react-query';
import { WEBSITE_ID } from '../projectConfig';
import { appointmentKey } from '../types';

const ROUTE_MISS_RETRY_MS = 15_000;

const EmployeeAppointmentPage: React.FC = () => {
    const { appointmentId } = useParams();
    const navigate = useNavigate();
    const queryClient = useQueryClient();

    const [matchedAppointment, setMatchedAppointment] = useState<Appointment | null>(null);
    const [openErrorDialog, setOpenErrorDialog] = useState(false);
    const [errorMessage, setErrorMessage] = useState<string | null>(null);
    const [handoffSuccessDialogOpen, setHandoffSuccessDialogOpen] = useState(false);
    const [isResolvingRouteAppointment, setIsResolvingRouteAppointment] = useState(false);
    const routeMissCacheRef = useRef<Map<string, number>>(new Map());

    const {
        currentEmployeeQuery,
        hydrateAppointmentsQuery,
        myAppointments,
        refreshAppointmentInCacheWithStatus,
    } = useAdminData();


    const isLoading = hydrateAppointmentsQuery.isLoading || isResolvingRouteAppointment;
    const isError = hydrateAppointmentsQuery.isError;

    const handleErrorDialogClose = () => {
        setOpenErrorDialog(false);
        setErrorMessage(null);
    };
    const formatRefreshError = (error: unknown): string => (
        error instanceof Error && error.message.trim().length > 0
            ? error.message
            : 'Unknown error while fetching appointment.'
    );

    useEffect(() => {
        if (!isError) {
            return;
        }
        const queryError = hydrateAppointmentsQuery.error?.message;
        setErrorMessage("Failed to load data: " + queryError);
        setOpenErrorDialog(true);
    }, [isError, hydrateAppointmentsQuery.error]);

    useEffect(() => {
        let cancelled = false;

        const currentEmployeeId = currentEmployeeQuery.data?.id;
        const canViewAllAppointments = currentEmployeeQuery.data?.admin === true;
        const canAccessAppointment = (appointment: Appointment) => (
            canViewAllAppointments || appointment.employeeID === currentEmployeeId
        );

        const resolveAppointment = async () => {
            if (!appointmentId) {
                setIsResolvingRouteAppointment(false);
                setMatchedAppointment(null);
                return;
            }

            const foundInMyAppointments = myAppointments.find((app) => app.id === appointmentId);
            if (foundInMyAppointments) {
                setIsResolvingRouteAppointment(false);
                setMatchedAppointment(foundInMyAppointments);
                return;
            }

            const cachedAppointment = queryClient.getQueryData(
                appointmentKey(WEBSITE_ID, appointmentId)
            ) as Appointment | undefined;
            if (cachedAppointment && canAccessAppointment(cachedAppointment)) {
                setIsResolvingRouteAppointment(false);
                setMatchedAppointment(cachedAppointment);
                return;
            }

            if (!canViewAllAppointments && !currentEmployeeId) {
                setIsResolvingRouteAppointment(false);
                setMatchedAppointment(null);
                return;
            }
            const lastMissAt = routeMissCacheRef.current.get(appointmentId);
            if (typeof lastMissAt === 'number' && Date.now() - lastMissAt < ROUTE_MISS_RETRY_MS) {
                setIsResolvingRouteAppointment(false);
                setMatchedAppointment(null);
                return;
            }

            setIsResolvingRouteAppointment(true);
            try {
                const refreshed = await refreshAppointmentInCacheWithStatus(appointmentId);
                if (!cancelled) {
                    if (refreshed.status === 'error') {
                        setMatchedAppointment(null);
                        setErrorMessage(`Failed to load appointment: ${formatRefreshError(refreshed.error)}`);
                        setOpenErrorDialog(true);
                        return;
                    }
                    const fetched = refreshed.status === 'ok' ? refreshed.appointment : null;
                    const nextMatched = fetched && canAccessAppointment(fetched) ? fetched : null;
                    setMatchedAppointment(nextMatched);
                    if (refreshed.status === 'ok' && nextMatched) {
                        routeMissCacheRef.current.delete(appointmentId);
                    }
                    if (refreshed.status === 'not_found' || (refreshed.status === 'ok' && nextMatched == null)) {
                        routeMissCacheRef.current.set(appointmentId, Date.now());
                    }
                }
            } finally {
                if (!cancelled) {
                    setIsResolvingRouteAppointment(false);
                }
            }
        };

        void resolveAppointment();
        return () => {
            cancelled = true;
        };
    }, [appointmentId, currentEmployeeQuery.data, myAppointments, queryClient, refreshAppointmentInCacheWithStatus]);

    return (
        <Box sx={{}}>
            <ErrorDialog open={openErrorDialog} onClose={handleErrorDialogClose} message={errorMessage} />
            <Box sx={{ mb: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}>
                <Button
                    variant="contained"
                    color="secondary"
                    onClick={() => (navigate('/admin/schedule-hub'))}
                    sx={{
                        borderRadius: '4px',
                        border: '2px solid rgba(255,255,255,0.15)',
                        fontWeight: '600',
                    }}
                >
                    Back
                </Button>
            </Box>
            {isLoading ? (
                <LoadingSpinner />
            ) : matchedAppointment ? (
                <Container>
                    <Box mb={4}>
                        <AppointmentCard
                            key={matchedAppointment.id}
                            appointment={matchedAppointment}
                            onHandoffSuccess={() => setHandoffSuccessDialogOpen(true)}
                        />
                    </Box>
                </Container>
            ) : (
                <Typography color="error" variant="h6" align="center" mt={4}>
                    Appointment not found or not accessible.
                </Typography>
            )}
            <Dialog
                open={handoffSuccessDialogOpen}
                onClose={() => setHandoffSuccessDialogOpen(false)}
                fullWidth
                maxWidth="xs"
            >
                <DialogTitle>Appointment Handed Off</DialogTitle>
                <DialogContent>
                    <Typography variant="body1">
                        The appointment was successfully handed off to the selected staff member.
                    </Typography>
                </DialogContent>
                <DialogActions>
                    <Button
                        onClick={() => setHandoffSuccessDialogOpen(false)}
                        color="primary"
                        variant="contained"
                    >
                        OK
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default EmployeeAppointmentPage;
